import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AdminDashboard from 'layouts/AdminDashboard';
import {
  clonePlan,
  createPlan,
  FeatureConfig,
  listPlans,
  PlanRecord,
  PlanRequest,
  publishPlan,
  retirePlan,
  updatePlan,
} from './PlanAdminService';

// ─── constants ────────────────────────────────────────────────────────────────

const ALL_FEATURE_KEYS = [
  'money.snapshot',
  'money.ratio',
  'money.transactions',
  'money.coach',
  'money.forecast',
  'money.csv_import',
  'goals.core',
  'household.core',
  'business.core',
];

const TIERS = ['free', 'plus', 'family', 'business'];

type StatusTab = 'ALL' | 'DRAFT' | 'PUBLISHED' | 'RETIRED';

// ─── helpers ──────────────────────────────────────────────────────────────────

function tierColor(
  tier: string,
): 'default' | 'primary' | 'success' | 'warning' {
  if (tier === 'plus') return 'primary';
  if (tier === 'family') return 'success';
  if (tier === 'business') return 'warning';
  return 'default';
}

function statusColor(
  status: string,
): 'default' | 'success' | 'error' {
  if (status === 'PUBLISHED') return 'success';
  if (status === 'RETIRED') return 'error';
  return 'default';
}

function formatPrice(val: number | null | undefined): string {
  if (val == null) return '—';
  return `£${Number(val).toFixed(2)}`;
}

function buildDefaultFeatures(): FeatureConfig[] {
  return ALL_FEATURE_KEYS.map((k) => ({ featureKey: k, enabled: false, usageLimit: null }));
}

function mergeFeatures(existing: FeatureConfig[]): FeatureConfig[] {
  return ALL_FEATURE_KEYS.map((k) => {
    const found = existing.find((f) => f.featureKey === k);
    return found ?? { featureKey: k, enabled: false, usageLimit: null };
  });
}

// ─── EditDialog ───────────────────────────────────────────────────────────────

interface EditDialogProps {
  plan: PlanRecord | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

function EditDialog({ plan, open, onClose, onSaved }: EditDialogProps) {
  const isCreate = plan === null;

  const [tier, setTier] = useState('free');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [priceMonthly, setPriceMonthly] = useState('');
  const [priceAnnual, setPriceAnnual] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [features, setFeatures] = useState<FeatureConfig[]>(buildDefaultFeatures());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (plan) {
        setTier(plan.tier);
        setDisplayName(plan.displayName ?? '');
        setDescription(plan.description ?? '');
        setPriceMonthly(plan.priceMonthly != null ? String(plan.priceMonthly) : '');
        setPriceAnnual(plan.priceAnnual != null ? String(plan.priceAnnual) : '');
        setCurrency(plan.currency ?? 'GBP');
        setFeatures(mergeFeatures(plan.features));
      } else {
        setTier('free');
        setDisplayName('');
        setDescription('');
        setPriceMonthly('');
        setPriceAnnual('');
        setCurrency('GBP');
        setFeatures(buildDefaultFeatures());
      }
      setError('');
    }
  }, [open, plan]);

  function toggleFeature(key: string) {
    setFeatures((prev) =>
      prev.map((f) =>
        f.featureKey === key ? { ...f, enabled: !f.enabled, usageLimit: !f.enabled ? f.usageLimit : null } : f,
      ),
    );
  }

  function setLimit(key: string, val: string) {
    const parsed = val === '' ? null : parseInt(val, 10);
    setFeatures((prev) =>
      prev.map((f) => (f.featureKey === key ? { ...f, usageLimit: parsed } : f)),
    );
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    const req: PlanRequest = {
      ...(isCreate ? { tier } : {}),
      displayName: displayName || undefined,
      description: description || undefined,
      priceMonthly: priceMonthly !== '' ? parseFloat(priceMonthly) : null,
      priceAnnual: priceAnnual !== '' ? parseFloat(priceAnnual) : null,
      currency,
      features: features.map((f) => ({
        featureKey: f.featureKey,
        enabled: f.enabled,
        usageLimit: f.usageLimit,
      })),
    };
    try {
      if (isCreate) {
        await createPlan(req);
      } else {
        await updatePlan(plan!.id, req);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Could not save plan.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isCreate ? 'New Draft Plan' : `Edit Plan — ${plan?.displayName}`}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isCreate && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Tier</InputLabel>
            <Select value={tier} label="Tier" onChange={(e) => setTier(e.target.value)}>
              {TIERS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Display Name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />

        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <TextField
            label="Monthly price"
            value={priceMonthly}
            onChange={(e) => setPriceMonthly(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            placeholder="e.g. 5.99"
          />
          <TextField
            label="Annual price"
            value={priceAnnual}
            onChange={(e) => setPriceAnnual(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
            placeholder="e.g. 59.99"
          />
          <TextField
            label="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            size="small"
            sx={{ width: 100 }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Features
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="right">Usage limit (blank = unlimited)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((f) => (
              <TableRow key={f.featureKey}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{f.featureKey}</TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={f.enabled}
                    onChange={() => toggleFeature(f.featureKey)}
                  />
                </TableCell>
                <TableCell align="right">
                  {f.enabled && (
                    <TextField
                      size="small"
                      value={f.usageLimit ?? ''}
                      onChange={(e) => setLimit(f.featureKey, e.target.value)}
                      inputProps={{ style: { width: 80, textAlign: 'right' }, min: 1 }}
                      type="number"
                      placeholder="∞"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} /> : isCreate ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── ConfirmDialog ─────────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  confirmColor?: 'primary' | 'error' | 'warning';
  busy: boolean;
  error: string;
  onConfirm: () => void;
  onClose: () => void;
}

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  confirmColor = 'primary',
  busy,
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">{body}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={busy}
        >
          {busy ? <CircularProgress size={18} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── PlanAdminPage ─────────────────────────────────────────────────────────────

export default function PlanAdminPage() {
  const [plans, setPlans] = useState<PlanRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState<StatusTab>('ALL');

  const [editTarget, setEditTarget] = useState<PlanRecord | null | undefined>(undefined);
  const [publishTarget, setPublishTarget] = useState<PlanRecord | null>(null);
  const [retireTarget, setRetireTarget] = useState<PlanRecord | null>(null);

  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    listPlans()
      .then((r) => setPlans(r.data))
      .catch(() => setLoadError('Could not load plans.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = plans.filter((p) => tab === 'ALL' || p.status === tab);

  async function handlePublish() {
    if (!publishTarget) return;
    setActionBusy(true);
    setActionError('');
    try {
      await publishPlan(publishTarget.id);
      setPublishTarget(null);
      load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? 'Could not publish plan.');
    } finally {
      setActionBusy(false);
    }
  }

  async function handleRetire() {
    if (!retireTarget) return;
    setActionBusy(true);
    setActionError('');
    try {
      await retirePlan(retireTarget.id);
      setRetireTarget(null);
      load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? 'Could not retire plan.');
    } finally {
      setActionBusy(false);
    }
  }

  async function handleClone(plan: PlanRecord) {
    try {
      await clonePlan(plan.id);
      load();
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? 'Could not clone plan.');
    }
  }

  return (
    <AdminDashboard>
      <Container maxWidth="xl" sx={{ paddingY: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={700}>
            Plan Configuration
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => setEditTarget(null)}
          >
            New Plan
          </Button>
        </Box>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" value="ALL" />
          <Tab label="Draft" value="DRAFT" />
          <Tab label="Published" value="PUBLISHED" />
          <Tab label="Retired" value="RETIRED" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tier</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Ver</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Monthly</TableCell>
                <TableCell align="right">Annual</TableCell>
                <TableCell align="center">Features</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No plans found.
                  </TableCell>
                </TableRow>
              )}
              {visible.map((plan) => {
                const enabledCount = plan.features.filter((f) => f.enabled).length;
                return (
                  <TableRow key={plan.id} hover>
                    <TableCell>
                      <Chip
                        label={plan.tier}
                        color={tierColor(plan.tier)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {plan.displayName}
                      </Typography>
                      {plan.description && (
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        v{plan.version}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.status}
                        color={statusColor(plan.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatPrice(plan.priceMonthly)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{formatPrice(plan.priceAnnual)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="text.secondary">
                        {enabledCount}/{plan.features.length}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.5} justifyContent="flex-end">
                        {plan.status === 'DRAFT' && (
                          <>
                            <Button
                              size="small"
                              onClick={() => setEditTarget(plan)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              color="success"
                              onClick={() => {
                                setActionError('');
                                setPublishTarget(plan);
                              }}
                            >
                              Publish
                            </Button>
                          </>
                        )}
                        {plan.status === 'PUBLISHED' && (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setActionError('');
                              setRetireTarget(plan);
                            }}
                          >
                            Retire
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleClone(plan)}
                        >
                          Clone
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Container>

      {/* Edit / Create dialog — undefined means closed, null means create new */}
      {editTarget !== undefined && (
        <EditDialog
          plan={editTarget}
          open={editTarget !== undefined}
          onClose={() => setEditTarget(undefined)}
          onSaved={load}
        />
      )}

      <ConfirmDialog
        open={publishTarget !== null}
        title="Publish Plan"
        body={`Publish "${publishTarget?.displayName}"? Any existing published plan for the ${publishTarget?.tier} tier will be automatically retired.`}
        confirmLabel="Publish"
        confirmColor="primary"
        busy={actionBusy}
        error={actionError}
        onConfirm={handlePublish}
        onClose={() => setPublishTarget(null)}
      />

      <ConfirmDialog
        open={retireTarget !== null}
        title="Retire Plan"
        body={`Retire "${retireTarget?.displayName}"? Existing subscribers keep their access until their billing cycle ends.`}
        confirmLabel="Retire"
        confirmColor="error"
        busy={actionBusy}
        error={actionError}
        onConfirm={handleRetire}
        onClose={() => setRetireTarget(null)}
      />
    </AdminDashboard>
  );
}
